import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { template } from "lodash";
import parse from "html-react-parser";
import cn from "classnames";

import {
    Uploader,
    Button,
    Heading,
    Image,
    Text,
    VStack,
    HStack,
    Placeholder,
} from "rsuite";
import { FileType } from "rsuite/esm/Uploader";
import { UploaderProps } from "rsuite/esm/Uploader/Uploader";
import ImportStorageIcon from "@rsuite/icons/ImportStorage";

import { getFileName } from "utils/url-helper";

import "./Logo.less";

const types: string[] = ["image/jpeg", "image/png"];

const acceptable = types.join(",");

interface Props extends UploaderProps {
    src: string;
    defaultName: string;

    onUpdate?(file: FileType): Promise<void>;
}

const Logo: React.FC<Props> = ({
    defaultName,
    src,
    className,
    onUpdate,
    ...props
}: Props) => {
    const { t } = useTranslation();

    const [uploading, setUploading] = useState<boolean>(false);

    const name = getFileName(new URL(src));

    const title = t("tenantConfig.featureConfigurations.logoSection.title");
    const subTitle = parse(
        template(
            t(
                "tenantConfig.featureConfigurations.logoSection.additionalInformation"
            )
        )({
            fileName: defaultName,
        })
    );

    const handleUpdate = async (fileList: FileType[]) => {
        setUploading(true);

        const file = fileList.pop();
        if (file) {
            await onUpdate?.(file);
        }

        setUploading(false);
    };

    return (
        <VStack className={cn(className, "logo__container")} spacing={5}>
            <VStack.Item>
                <Heading className={"logo__heading"} level={2}>
                    {title}
                </Heading>
            </VStack.Item>

            <VStack.Item>
                <Heading className={"logo__heading"} level={4}>
                    {subTitle}
                </Heading>
            </VStack.Item>

            <VStack.Item style={{ marginTop: "1rem" }}>
                {uploading ? (
                    <Placeholder.Graph
                        className={"logo__content"}
                        active={uploading}
                    />
                ) : (
                    <Uploader
                        className={"logo__content"}
                        draggable={true}
                        autoUpload={false}
                        fileListVisible={false}
                        accept={acceptable}
                        listType="picture-text"
                        onChange={handleUpdate}
                        {...props}
                    >
                        <Button>
                            <HStack
                                style={{ height: "100%" }}
                                justifyContent={"space-between"}
                            >
                                <HStack.Item alignSelf={"stretch"}>
                                    <HStack
                                        style={{ height: "100%" }}
                                        justifyContent={"flex-start"}
                                        spacing={20}
                                    >
                                        <HStack.Item alignSelf={"stretch"}>
                                            <Image
                                                src={`${src}?t=${Date.now()}`}
                                                className="logo__image"
                                            />
                                        </HStack.Item>

                                        <HStack.Item alignSelf={"center"}>
                                            <Text>{name}</Text>
                                        </HStack.Item>
                                    </HStack>
                                </HStack.Item>

                                <HStack.Item alignSelf={"center"}>
                                    <ImportStorageIcon
                                        className={"logo__uploader_icon"}
                                    />
                                </HStack.Item>
                            </HStack>
                        </Button>
                    </Uploader>
                )}
            </VStack.Item>
        </VStack>
    );
};

export default Logo;
